/**
 * The feedback/debug transport (#7/#8) — `POST /mark/feedback`.
 *
 * WHY NOT THE OUTBOX. Feedback used to ride the capture outbox as an Event with
 * `skill: "feedback"`. The coordinator's ROUTING_TABLE has no such skill, so
 * `CaptureLogged` routed to `daily_field_log_capture` and the route-conflict
 * check rejected every submission with a permanent 422 — while the screen said
 * "Thanks — sent." Verified live on 2026-07-27. A field report is BuildEngine's,
 * not part of the tenant's project record; it gets its own endpoint.
 *
 * The confirmation is only as true as the server's answer, so this returns a
 * tagged outcome (same shape as read-client's `ReadOutcome`) and the caller must
 * decide what to say. There is no local queue: if the send fails, the typed text
 * stays in the box and the screen says so plainly, which is honest and needs no
 * second outbox to maintain. Silently queueing would risk the same class of bug
 * this replaces — a report the app believes it sent and nobody ever receives.
 */

import type { TransportConfig } from "../outbox/http-transport";
import { deviceToken } from "../identity/device-token";
import { credentialFetch as fetch } from "../auth/credential-fetch";

export type ReportKind = "feedback" | "debug";

export type SendOutcome =
  | { ok: true; stored: true; emailed: boolean }
  /** `retryable` true = try again later (offline, 5xx); false = the server
   * refused this report and retrying will not help. */
  | { ok: false; error: string; retryable: boolean };

export interface FeedbackReport {
  kind: ReportKind;
  project_ref: string;
  message?: string;
  diagnostics?: unknown;
}

export interface FeedbackClient {
  send(report: FeedbackReport): Promise<SendOutcome>;
}

export class HttpFeedbackClient implements FeedbackClient {
  constructor(private readonly config: TransportConfig) {}

  async send(report: FeedbackReport): Promise<SendOutcome> {
    let res: Response;
    try {
      res = await fetch(`${this.config.endpoint}/mark/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.config.token}`,
        },
        credentials: "include",
        body: JSON.stringify(report),
      });
    } catch {
      // No signal on a job site is the normal case, not an incident.
      return { ok: false, error: "offline", retryable: true };
    }

    if (!res.ok) {
      let error = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) error = body.error;
      } catch {
        // Keep the status-derived code.
      }
      // 5xx (and the coordinator's 503 transient) are worth retrying; a 4xx
      // means this report will never be accepted as-is.
      return { ok: false, error, retryable: res.status >= 500 };
    }

    try {
      const body = (await res.json()) as { payload?: { stored?: boolean; emailed?: boolean } };
      // A 200 whose body does not claim `stored` is not a success we can vouch
      // for — treat it as retryable rather than telling the human it landed.
      if (body?.payload?.stored !== true) {
        return { ok: false, error: "not_stored", retryable: true };
      }
      return { ok: true, stored: true, emailed: body.payload.emailed === true };
    } catch {
      return { ok: false, error: "malformed_response", retryable: true };
    }
  }
}

/** Returns `null` when no endpoint is baked in (dev/test builds), so callers can
 * degrade rather than throw — the same contract `makeReadClient` uses. */
export function makeFeedbackClient(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): FeedbackClient | null {
  const endpoint = env.VITE_MARK_ENDPOINT;
  if (!endpoint) return null;
  return new HttpFeedbackClient({
    endpoint: endpoint.replace(/\/+$/, ""),
    // Feedback authenticates as the human sending it, so a report of a
    // problem is attributable to the person who hit it.
    token: env.VITE_MARK_AUTH_STUB === "1"
      ? deviceToken()?.trim() || env.VITE_MARK_BEARER || ""
      : "",
  });
}
